import type { CompanySiteType } from '@api';
import { ActionsCell, ContactDetails, useFormatAddress } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';

const TRANS_PATH = 'COMPANY_SITES.TABLE';

const TRANS_PATH_ACTIONS = 'COMPANY_SITES.TABLE.ACTIONS';

type UseCompanySitesTableColumnsPropsType = {
  handleDeleteCompanySite: (siteId: string) => void;
  handleEditCompanySite: (site: CompanySiteType) => void;
  handleSetSiteAsHeadquarter: (siteId: string) => void;
};

type UseCompanySitesTableColumnsReturnType = {
  columns: DataTableColumn<CompanySiteType>[];
};

export const useCompanySitesTableColumns = ({
  handleDeleteCompanySite,
  handleEditCompanySite,
  handleSetSiteAsHeadquarter,
}: UseCompanySitesTableColumnsPropsType): UseCompanySitesTableColumnsReturnType => {
  const { t } = useTranslation('companyDetailsGeneral');
  const { formatAddressMultiLine } = useFormatAddress();
  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER', { ns: 'common' });

  const columns: DataTableColumn<CompanySiteType>[] = [
    {
      accessor: 'address',
      title: t(`${TRANS_PATH}.COLUMNS.ADDRESS`),
      render: ({ address }) => {
        const formatted = formatAddressMultiLine(address);
        return formatted ?? noDataPlaceholder;
      },
      cellsStyle: () => ({ whiteSpace: 'pre-line' }),
    },
    {
      accessor: 'contact',
      title: t(`${TRANS_PATH}.COLUMNS.CONTACT_DETAILS`),
      render: ({ contact }) => <ContactDetails contact={contact} />,
    },
    {
      accessor: 'employeeCount',
      title: t(`${TRANS_PATH}.COLUMNS.EMPLOYEES`),
      render: ({ employeeCount }) =>
        t(`${TRANS_PATH}.EMPLOYEES_COUNT`, { count: employeeCount }),
    },
    {
      accessor: 'actions',
      title: t(`${TRANS_PATH}.COLUMNS.ACTIONS`),
      titleStyle: { textAlign: 'center' },
      render: (record) => (
        <ActionsCell.Root>
          <ActionsCell.StarAction
            isActive={record.isHeadquarter}
            tooltipActive={t(`${TRANS_PATH_ACTIONS}.STAR_TOOLTIP_ACTIVE`)}
            tooltipInactive={t(`${TRANS_PATH_ACTIONS}.STAR_TOOLTIP_INACTIVE`)}
            onClick={() => handleSetSiteAsHeadquarter(record.id)}
          />
          <ActionsCell.Edit onClick={() => handleEditCompanySite(record)} />
          <ActionsCell.Delete
            onClick={() => handleDeleteCompanySite(record.id)}
          />
        </ActionsCell.Root>
      ),
      width: 100,
    },
  ];

  return { columns };
};
